import { notification } from 'antd';
import {
  useBookInspectionMutation,
  useLazyGetInspectionQuery,
} from 'app/features/inspection/api';
import useUploadFile from 'app/hooks/useUploadFile';
import { AuthContext } from 'app/providers/Authentication';
import React, { useContext } from 'react';
import { useNavigate, useParams } from 'react-router';
import Header from './components/Header';
import InspectionCriteria from './components/Steps/InspectionCriteria';
import ReviewAndSubmit from './components/Steps/ReviewSubmit';
import SelectInspector from './components/Steps/SelectInspector';
import { isEmpty, isString } from 'lodash';
import FormStepper from 'app/components/Form/FormStepper';
import {
  extractChangedValues,
  parseInspection,
  transformPayloadInspection,
} from './util';
import { Spinner } from 'app/components/Spinner';
import * as Yup from 'yup';
import moment, { isMoment, Moment } from 'moment';
import Modal from 'app/components/Modal';
import {
  InspectionContext,
  InspectionProvider,
} from './context/InspectionContext';
import BookInspectionContainer from './components/containers/BookInspectionContainers';
import { TrackingRequestContext } from 'app/context/tracking-request';

const BookInspection = () => {
  const { isRequesting } = useContext(TrackingRequestContext);
  const navigate = useNavigate();
  const authContext = React.useContext(AuthContext);
  const { isLoading: isUploading, uploadFile } = useUploadFile();
  const [fetchInspection, result] = useLazyGetInspectionQuery();
  const [bookInspection, stateBookInspection] = useBookInspectionMutation();
  const [currentInspection, setCurrentInspection] = React.useState(null);
  const [currentStep, setCurrentStep] = React.useState(0);
  const params = useParams();

  const steps = [
    {
      key: 'review-submit',
      title: 'Review & submit',
      content: <ReviewAndSubmit></ReviewAndSubmit>,
    },
    {
      key: 'inspection-criteria',
      title: 'Inspection Criteria',
      content: <InspectionCriteria></InspectionCriteria>,
    },
    {
      key: 'select-inspector',
      title: 'Select Inspector',
      content: (
        <SelectInspector
          inspectionId={params.inspectionId || ''}
        ></SelectInspector>
      ),
    },
  ];

  const initialValues = React.useMemo(() => {
    if (isEmpty(result.data)) return null;
    return parseInspection(result.data);
  }, [result]);

  async function handleUploadDocument(list) {
    if (!Array.isArray(list)) return [];
    if (list.length === 0) return [];
    const uploaded = list.filter(({ originFileObj }) => !originFileObj);
    const uploadFiles = list
      .filter(({ originFileObj }) => originFileObj)
      .map(({ originFileObj }) => originFileObj);
    let uploadedFiles = [];
    if (uploadFiles.length > 0) {
      uploadedFiles = await uploadFile(uploadFiles, authContext.company.id);
    }
    return [...uploaded, ...uploadedFiles];
  }

  async function submitForm(values, draft) {
    // Check Field Upload.
    const { additionalInformation } = values;
    /* The commented line `// const uploaded = await handleUploadDocument(documentLists);` is calling
    the `handleUploadDocument` function with the `documentLists` as a parameter and awaiting its
    result. This function is responsible for processing a list of documents for upload. */
    const { documentLists } = additionalInformation;
    const uploaded = await handleUploadDocument(documentLists);
    const transform = transformPayloadInspection(
      {
        ...values,
        additionalInformation: {
          ...additionalInformation,
          documentLists: uploaded,
        },
      },
      result.data,
    );
    try {
      await bookInspection({
        inspectionId: params.inspectionId,
        data: {
          ...transform,
          isPublished: !draft,
        },
      }).unwrap();
      fetchInspection(params.inspectionId);
      notification.success({
        message: !draft ? 'Book Inspection updated' : 'Draft saved',
        placement: 'bottomLeft',
      });
      if (!draft) {
        navigate('/dashboard', { replace: true });
      }
    } catch (e) {
      notification.error({
        message: !draft
          ? 'Book Inspection updated failed'
          : 'Draft save failed',
        placement: 'bottomLeft',
      });
      throw e;
    }
  }

  async function handleSubmitForm(values, meta) {
    const { action, formikAction } = meta;
    if (action === 'next') {
      formikAction.setSubmitting(false);
    } else {
      try {
        // Save Draft or Submit.
        await submitForm(values, action === 'save');
        formikAction.setSubmitting(false);
      } catch (error) {
        // Trace Error.
        console.error(error);
        formikAction.setSubmitting(false);
      }
    }
  }

  const handleChangeGlobalAQL = (value, prevValue, formikProps) => {
    const { values } = formikProps;

    const { criticalLevel, criticalMajorLevel, criticalMinorLevel } = value;
    const {
      criticalLevel: criticalLevelPrev,
      criticalMajorLevel: majorLevelPrev,
      criticalMinorLevel: minorLevelPrev,
    } = prevValue;

    const currentValues = {
      criticalLevel,
      criticalMajorLevel,
      criticalMinorLevel,
    };
    const previousValues = {
      criticalLevel: criticalLevelPrev,
      criticalMajorLevel: majorLevelPrev,
      criticalMinorLevel: minorLevelPrev,
    };

    const AQL = extractChangedValues(currentValues, previousValues);

    if (isEmpty(AQL)) return;
    if (!Array.isArray(values.inspectionPOItems)) return;
    // Set AQL to all products
    const updatedAllProducts = values.inspectionPOItems.map((item) => {
      return {
        ...item,
        inspectionAQL: {
          ...item.inspectionAQL,
          ...AQL,
        },
      };
    });
    formikProps.setFieldValue('inspectionPOItems', updatedAllProducts);
  };

  const handleChangeInspectionDateRange = (value, prevValue) => {
    const { inspectionDate, inspectionEndDate, assignTo } = value;
    const { inspectionDate: prevStartDate, inspectionEndDate: prevEndDate } =
      prevValue;

    if (!assignTo) return;

    if (inspectionDate === prevStartDate && inspectionEndDate === prevEndDate)
      return;
    const mStartDate = moment(inspectionDate, 'DD/MM/YYYY');
    const mEndDate = moment(inspectionEndDate, 'DD/MM/YYYY');

    const isDateValid = assignTo.every((item) => {
      const { inspectionDates } = item;
      return inspectionDates.every(({ date }: { date: Moment | string }) => {
        if (!date) return true;
        let mDate: Moment;
        if (isString(date)) {
          mDate = moment(new Date(date), 'DD/MM/YYYY');
        } else {
          mDate = date;
        }
        return (
          mStartDate.isSameOrBefore(mDate, 'd') &&
          mEndDate.isSameOrAfter(mDate, 'd')
        );
      });
    });
    if (isDateValid) return;
    //Show Warning.
    Modal.danger({
      title: 'Inspector scheduled date out of range',
      content: 'Please arrange again.',
      cancelButtonProps: { hidden: true },
    });
  };

  function handleFormChange(
    changedValue: any,
    _: any,
    prevValues,
    formikProps,
  ) {
    setCurrentInspection(formikProps.values);
    if (Object.keys(changedValue).length === 0) return;
    const keyChanged = Object.keys(changedValue);
    if (keyChanged[0] === 'inspectionRequirement') {
      handleChangeGlobalAQL(
        changedValue.inspectionRequirement,
        prevValues.inspectionRequirement,
        formikProps,
      );
      handleChangeInspectionDateRange(
        changedValue.inspectionRequirement,
        prevValues.inspectionRequirement,
      );
    }
  }

  React.useEffect(() => {
    fetchInspection(params.inspectionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Header
        onBack={() => {
          if (currentStep <= 0) {
            navigate(`/dashboard/inspection/job/${params.inspectionId}`);
          } else {
            setCurrentStep(currentStep - 1);
          }
        }}
      ></Header>
      {result.isUninitialized || result.isFetching ? (
        <Spinner visible />
      ) : (
        <InspectionProvider inspection={currentInspection || initialValues}>
          <FormStepper
            validationStepSchema={(current) => {
              if (current + 1 >= steps.length - 1) {
                //Last Step

                return Yup.object().shape({
                  inspectionRequirement: Yup.object().shape({
                    noOfManday: Yup.number().required(
                      'No of Manday is required',
                    ),
                    inspectionEndDate: Yup.string().required(
                      'Inspection End Date is required',
                    ),
                    inspectionDate: Yup.string()
                      .required('Inspection Date is required')
                      .test(
                        'inspectionDate',
                        'Inspection Start Date must be before Inspection End Date',
                        function (value) {
                          const { inspectionEndDate } = this.parent;
                          if (!inspectionEndDate || !value) return true;
                          const startDate = moment(value, 'DD/MM/YYYY');
                          const endDate = moment(
                            inspectionEndDate,
                            'DD/MM/YYYY',
                          );
                          return startDate.isSameOrBefore(endDate);
                        },
                      ),
                    assignTo: Yup.array().required('Inspectors is required'),
                  }),
                });
              }
              return undefined;
            }}
            nextButtonProps={{
              disabled: isRequesting,
            }}
            onChangeStep={(current, next) => {
              return next <= current;
            }}
            saveButtonProps={{ hidden: true }}
            loading={isUploading || stateBookInspection.isLoading}
            initialValues={initialValues}
            onValuesChange={handleFormChange}
            enableReinitialize
            steps={steps}
            onSubmit={handleSubmitForm}
            shouldNext={(current) => {
              return current + 1 <= steps.length - 1;
            }}
            stepProps={{
              labelPlacement: 'vertical',
              size: 'default',
            }}
          />
        </InspectionProvider>
      )}
    </div>
  );
};

const WithBookInspectionContainer = () => {
  return (
    <BookInspectionContainer>
      <BookInspection />
    </BookInspectionContainer>
  );
};

export default React.memo(WithBookInspectionContainer);
